import React, {useEffect, useMemo, useRef, useState} from "react";
import Skeleton from "../skeleton/Skeleton";
import CollapsableBox from "../collapsableBox/CollapsableBox";
import "./paymentsSection.css";
import useAppContext from "../../hooks/useAppContext";
import useUserContext from "../../hooks/useUserContext";
import {getShopTotalDuePayments} from "../../api/getShopTotalDuePayments";
import {Button} from "antd";
import useToast from "../../hooks/useToast";
import PaymentModal from "./PaymentModal";
import {formatDateNoTime} from "../../utils/friendlyDate";
import {getShopTotalFundsAdded} from "../../api/getShopTotalFundsAdded";
import {useLocation, useNavigate} from "react-router-dom";
import {getShopFundsAdded} from "../../api/getShopFundsAdded";
import Loader from "../loader/Loader";
import PricingPlanModal from "./PricingPlanModal";
import {CoinIcon} from "../../assets/icons/CoinIcon";
import CreditModal from "./CreditModal";
import useCredits from "../../hooks/useCredits";
import useGlobalModal from "../../hooks/useGlobalModal";
import ShopifyPaymentModal from "./ShopifyPaymentModal";

interface PaymentsSectionProps {
    showTitle?: boolean;
    isInitiallyOpen?: boolean;
}

const PaymentsSection = ({ isInitiallyOpen = false }: PaymentsSectionProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [duePayments, setDuePayments] = useState(0);
    const { selectedDomain } = useUserContext();
    const { data: { shop, shopifyBillingDetails } } = useAppContext();
    const isShopifyPlatform = shop?.data.platformId === 1;
    const commission = shop.data.commission;
    const messageCommission = shop.data.messageCost;
    const conversationCommission = shop.data.conversationCost;
    const pricingPlan = shop?.data?.pricingPlan;
    const phoneNumberCost = pricingPlan?.id === 1 ? shop.data.phoneNumberCost : pricingPlan?.isPhoneNumberIncluded ? 0 : 19;
    const isLoadingCommission = shop.isLoading
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const { showToast } = useToast();
    const [paymentsHistory, setPaymentsHistory] = useState<any[]>([]);
    const [totalPayments, setTotalPayments] = useState(0);
    const navigate = useNavigate();
    const params = useLocation().search;
    const openPaymentsModal = new URLSearchParams(params).get("open_payments_modal");
    const sectionRef = useRef<HTMLDivElement | null>(null);
    const { setIsCreditModalOpen } = useGlobalModal();

    useEffect(() => {
        if(openPaymentsModal === '1') setIsPaymentModalOpen(true);
    }, [openPaymentsModal])


    const getPayments = async () => {
        return selectedDomain ? getShopFundsAdded(selectedDomain) : null;
    }

    const loadPaymentsHistory = async () => {
        const response = await getPayments();
        response?.data && setPaymentsHistory(response.data.payments);
    }

    const loadDuePayments = async () => {
        const response = await getShopTotalDuePayments(selectedDomain!);
        setDuePayments(response.data.duePayments ? parseFloat(response.data.duePayments) : 0);
    }

    const loadTotalFundsAdded = async () => {
        const response = await getShopTotalFundsAdded(selectedDomain!);
        setTotalPayments(response.data.total ? parseFloat(response.data.total) : 0);
    }

    const loadData = async () => {
        // if(true || isShopifyPlatform) {
        //     setIsLoading(false);
        //     return;
        // }
        await Promise.all([
            // loadDuePayments(),
            // loadTotalFundsAdded(),
            loadPaymentsHistory(),
        ]);
        setIsLoading(false);
    }

    useEffect(() => {
        selectedDomain && shop?.data?.id && loadData();
    }, [selectedDomain, shop?.data?.id]);

    useEffect(() => {
        if(isInitiallyOpen) {
            sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isInitiallyOpen]);

    const toggleAddFundsModal = () => {
        setIsPaymentModalOpen(!isPaymentModalOpen);
    }

    const closeAddFundsModal = () => {
        setIsPaymentModalOpen(false);
    }

    const onAddFundsSubmission = (totalFundsAdded: number, id: string) => {
        setTotalPayments(totalPayments + totalFundsAdded);
        setPaymentsHistory([{amountUsd: totalFundsAdded, externalOrderId: id, createdAt: new Date()}, ...paymentsHistory]);
    }

    const onAddFundsToBalanceClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleAddFundsModal();
    }

    const goToOrders = () => {
        navigate('/orders');
    }

    const balanceTitle = useMemo(() => {
        if(isShopifyPlatform) {
            return 'Payment / Cap';
        } else {
            return 'Current Balance';
        }
    }, [selectedDomain, shop.data.platformId, isShopifyPlatform]);

    const balanceValue = useMemo(() => {
        if(isShopifyPlatform) {
            return `${Math.floor((shopifyBillingDetails?.data?.recurring_application_charge?.balance_used || 0) * 100)/100} / ${parseInt(shopifyBillingDetails.data.recurring_application_charge?.capped_amount || 0)}`;
        } else {
            return Math.round((totalPayments - duePayments) * 100) / 100;
        }
    }, [selectedDomain, shop.data.platformId, isShopifyPlatform, totalPayments, duePayments, shopifyBillingDetails.data.recurring_application_charge]);

    const latestFundsAddedTitle = useMemo(() => {
        if(isShopifyPlatform) {
            return 'Recent Billing Activity';
        } else {
            return 'Latest Funds Added';
        }
    }, [selectedDomain, shop]);

    const balance = parseInt(shopifyBillingDetails.data.recurring_application_charge?.capped_amount || 0);
    const balanceUsed = Math.floor((shopifyBillingDetails?.data?.recurring_application_charge?.balance_used || 0) * 100)/100;
    const balanceUsedPercent = balanceUsed/balance*100;
    const { credits, isPlanSupportCredits, allowedToPurchaseCredits, availableCredits } = useCredits();

    return (
        <CollapsableBox title="Payments" isInitiallyOpen={isInitiallyOpen}
            actions={<Button type="primary" size="large" onClick={onAddFundsToBalanceClick}>{'Update your Payment Plan'}</Button>}
            shortStatus={
                <div className={`short-status-payments ${isLoading || shop.isLoading ? '' : !isShopifyPlatform && totalPayments < duePayments ? 'negative' : 'positive'}`}>
                    {isLoading || shop.isLoading ? <Loader size={36} /> : `${pricingPlan.title} plan: ${isPlanSupportCredits ? `${credits.availableCredits} credits` : `${balanceValue} USD`}`}
                </div>
            }
        >
            <div ref={sectionRef} className="shop-payments-container">
                {isLoading || isLoadingCommission ?
                    <Skeleton type='block-grid' hasWhiteBackground={true} />
                    : <>
                        <div className='payments'>
                            {isPlanSupportCredits ? <div className="box summary">
                                    <div className="box-sub-table-title">Credit Balance</div>
                                    <div key="box-sub-table-used" className="box-sub-table-record">
                                        <div className="summary-label">Free credits left</div>
                                        <div className="summary-value">{credits?.freeLeft}</div>
                                    </div>
                                    <div key="box-sub-table-cap" className="box-sub-table-record">
                                        <div className="summary-label">Payed credits left</div>
                                        <div className="summary-value cap">{credits?.payedLeft}</div>
                                    </div>
                                    <div className="box-sub-table-record double">
                                        <Button disabled={!allowedToPurchaseCredits} onClick={() => setIsCreditModalOpen(true)} className="get-credits-btn" size="large" type="primary" icon={<CoinIcon width={24} height={24} color='#fff' />}>Get more credits!</Button>
                                    </div>
                                </div>
                            :
                                <div className="box summary">
                                    <div className="box-sub-table-title">Balance</div>
                                    <div key="box-sub-table-used" className="box-sub-table-record">
                                        <div className="summary-label">Current Use</div>
                                        <div className="summary-value">${balanceUsed.toLocaleString()}</div>
                                    </div>
                                    <div key="box-sub-table-cap" className="box-sub-table-record">
                                        <div className="summary-label">Monthly Cap</div>
                                        <div className="summary-value cap">${balance.toLocaleString()}{availableCredits ? ` + ${availableCredits} credits` : ''}</div>
                                    </div>
                                    <div key="box-sub-table-graphic" className="box-sub-table-record double">
                                        <div className="balance-graphic">
                                            <div
                                                className="balance-rect">{balanceUsedPercent < 65 ? `$${balance.toLocaleString()}` : ''}</div>
                                            <div className="used-rect-container">
                                                {balanceUsedPercent ? <div className="used-rect"
                                                                           style={{width: `${balanceUsedPercent}%`}}>{balanceUsedPercent > 45 ? `$${balanceUsed.toLocaleString()}` : ''}</div> : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                            {isPlanSupportCredits ? null :
                                <div className="box commissions-list">
                                    <div className="box-sub-table-title">{pricingPlan?.title || 'Prices List'}</div>
                                    <div key="box-sub-table-conversions" className="box-sub-table-record">
                                        <div className="commission-type">Conversion</div>
                                        <div
                                            className="commission-amount">{Math.floor((pricingPlan?.id ? pricingPlan?.recoveryCommission : commission) * 100)}%
                                        </div>
                                    </div>
                                    <div key="box-sub-table-messages" className="box-sub-table-record">
                                        <div className="commission-type">Template Message</div>
                                        <div className="commission-amount">${messageCommission}</div>
                                    </div>
                                    <div key="box-sub-table-conversations" className="box-sub-table-record">
                                        <div className="commission-type">Conversation Message</div>
                                        <div className="commission-amount">${conversationCommission}</div>
                                    </div>
                                    {phoneNumberCost ?
                                        <div key="box-sub-table-phone-number" className="box-sub-table-record">
                                            <div className="commission-type">Phone Number</div>
                                            <div className="commission-amount">${phoneNumberCost}</div>
                                        </div> :
                                        <div key="box-sub-table-phone-number" className="box-sub-table-record"/>}
                                </div>
                            }
                            <div className="box payments-history-container">
                                <div className="box-sub-table-title">{latestFundsAddedTitle}</div>
                                {paymentsHistory.length ? paymentsHistory.slice(0, 3).map(paymentHistory => {
                                    return (
                                        <div key={`box-sub-table-${paymentHistory.id}`} className="box-sub-table-record">
                                            <div className="payment-date">{formatDateNoTime(new Date(paymentHistory.createdAt))}</div>
                                            <div className="payment-amount">{`$${paymentHistory.amountUsd}`}</div>
                                            <div className="payment-order-id">{paymentHistory.entityType} {paymentHistory.entityId}</div>
                                        </div>
                                    );
                                }) : <div className="empty-state-payments">Looks like no payments were made so far</div>}
                                {paymentsHistory.length > 3 ? <div className="show-more"><span>View Last 30 Days</span></div> : null}
                            </div>
                        </div>
                        {/*<div className="bottom-actions">*/}
                        {/*    <Button onClick={goToOrders}>Orders</Button>*/}
                        {/*    <Button>Full Payments Log</Button>*/}
                        {/*</div>*/}
                    </>
                }
            </div>
            <PricingPlanModal isOpen={isPaymentModalOpen} toggle={toggleAddFundsModal} />

            {/* OLD SHOPIFY CAP SETTINGS MODAL: */}
            {/*<ShopifyPaymentModal isOpen={isPaymentModalOpen} totalPayments={totalPayments} duePayments={duePayments} onAddFunds={onAddFundsSubmission} toggle={toggleAddFundsModal} close={closeAddFundsModal} />*/}

            {/* OLD PAYPAL PAYMENT MODAL (CAP): */}
            {/*<PaymentModal isOpen={isPaymentModalOpen} totalPayments={totalPayments} duePayments={duePayments} onAddFunds={onAddFundsSubmission} toggle={toggleAddFundsModal} />*/}

        </CollapsableBox>
    )
}

export default PaymentsSection;